import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { claimClFarmRewardInteraction } from "@/domains/interactions"
import { SCALE } from "@/domains/tokens/static-tokens"
import { ClFarmRewardsView } from "@/features/rewards/components/cl-farm-rewards-view"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

export const ClFarmRewards = observer(() => {
  const account = useAccount()
  const chainConfig = useChainConfig()
  const config = useConfig()
  const emitter = useEventEmitter()
  const scaleApp = useScaleApplication()

  const clFarmsWithRewards = scaleApp.ClFarms.Repository.getAllWithGaugeRewards()

  const onClaimClFarmReward = async (farm: (typeof clFarmsWithRewards)[0]) => {
    try {
      if (!account.address) {
        throw new Error("no address found")
      }
      if (!farm.farmAddress || !farm.voterAddress) {
        throw new Error("no farm or voter address found")
      }

      await claimClFarmRewardInteraction({
        account: account.address,
        chainId: chainConfig.chainId,
        config,
        voterAddress: farm.voterAddress.toViemAddress() as `0x${string}`,
        farmAddress: farm.farmAddress.toViemAddress() as `0x${string}`,
        earnedAddresses: [(SCALE.address as any).toViemAddress?.() ?? SCALE.address] as Array<`0x${string}`>,
        pairDisplayName: farm.displayName ?? "unknown",
        emitter,
      })
      // MobX will automatically update UI when store data changes - no manual refetch needed
    } catch (error) {
      console.error("ClFarmRewards→onClaimClFarmReward error", error)
    }
  }

  const onClaimAllClFarmRewards = async (farms: typeof clFarmsWithRewards) => {
    try {
      if (!account.address) {
        throw new Error("no address found")
      }
      // For CL farms, we need to claim each one individually since they use different contracts
      // This could be optimized in the future with a batch claim function
      for (const farm of farms) {
        await onClaimClFarmReward(farm)
      }
    } catch (error) {
      console.error("ClFarmRewards→onClaimAllClFarmRewards error", error)
    }
  }

  return (
    <ClFarmRewardsView
      governanceToken={SCALE}
      farms={clFarmsWithRewards}
      onClaimAllClFarmRewards={onClaimAllClFarmRewards}
      onClaimClFarmReward={onClaimClFarmReward}
    />
  )
})
